/**
 * Online meeting / Zoom details must not live in listing publicData.
 *
 * Sharetribe listing extended data only accepts publicData, privateData, and
 * metadata. There is no listing.protectedData — writing it 400s
 * (validation-disallowed-key).
 *
 * Visibility:
 * - publicData: anyone (including anonymous listings.show)
 * - privateData: listing author only
 * - transaction.protectedData: both parties of that transaction
 *
 * Hosted Console still exposes the field as a public listing field, so the
 * edit form may submit `pub_onlineMeeting`. We persist to privateData and
 * null public copies so Sharetribe deletes the leaked keys. Booked customers
 * get the link from GET /api/online-meeting after the booking is accepted
 * or paid — never from transaction.protectedData on an unpaid request.
 */

export const ONLINE_MEETING_PUBLIC_KEYS = ['onlineMeeting', 'meetingLink'];
export const ONLINE_MEETING_FORM_KEYS = [
  'pub_onlineMeeting',
  'prot_onlineMeeting',
  'priv_onlineMeeting',
];

const isPresent = value => value != null && value !== '';

const readMeetingValue = (source = {}) => {
  if (!source || typeof source !== 'object') {
    return null;
  }
  if (isPresent(source.onlineMeeting)) {
    return source.onlineMeeting;
  }
  if (isPresent(source.meetingLink)) {
    return source.meetingLink;
  }
  return null;
};

/**
 * Author reads privateData. Leftover public / invalid listing.protectedData
 * are fallbacks for unmigrated records. Booked customers should use
 * transaction.protectedData or GET /api/online-meeting.
 */
export const getOnlineMeetingDetails = listing => {
  const attrs = listing?.attributes || {};
  return (
    readMeetingValue(attrs.privateData) ??
    readMeetingValue(attrs.protectedData) ??
    readMeetingValue(attrs.publicData) ??
    null
  );
};

export const getOnlineMeetingFromTransaction = transactionOrProtectedData => {
  const protectedData =
    transactionOrProtectedData?.attributes?.protectedData || transactionOrProtectedData || {};
  return readMeetingValue(protectedData);
};

export const stripPublicMeetingDetails = publicData => {
  if (!publicData || typeof publicData !== 'object') {
    return publicData;
  }
  return Object.keys(publicData).reduce((acc, key) => {
    if (!ONLINE_MEETING_PUBLIC_KEYS.includes(key)) {
      acc[key] = publicData[key];
    }
    return acc;
  }, {});
};

const hasPublicMeetingLeak = (publicData = {}) =>
  ONLINE_MEETING_PUBLIC_KEYS.some(key => isPresent(publicData?.[key]));

const firstFormMeetingValue = values => {
  if (!values || typeof values !== 'object') {
    return null;
  }
  for (const key of ONLINE_MEETING_FORM_KEYS) {
    if (isPresent(values[key])) {
      return values[key];
    }
  }
  return null;
};

const hasOwn = (obj, key) =>
  !!obj && typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, key);

const hasSubmittedMeetingField = (
  updateValues,
  incomingPublic,
  incomingPrivate,
  incomingProtected
) =>
  ONLINE_MEETING_FORM_KEYS.some(key => hasOwn(updateValues, key)) ||
  ONLINE_MEETING_PUBLIC_KEYS.some(key => hasOwn(incomingPublic, key)) ||
  hasOwn(incomingPrivate, 'onlineMeeting') ||
  hasOwn(incomingProtected, 'onlineMeeting');

/**
 * Hydrate the hosted public form field from privateData after migration.
 */
export const hydrateOnlineMeetingFormValues = (initialValues, listing) => {
  const details = getOnlineMeetingDetails(listing);
  if (!isPresent(details)) {
    return initialValues;
  }
  const alreadySet = ONLINE_MEETING_FORM_KEYS.some(key => isPresent(initialValues?.[key]));
  if (alreadySet) {
    return initialValues;
  }
  return {
    ...initialValues,
    pub_onlineMeeting: details,
  };
};

/**
 * Move meeting details to listing.privateData and send null for public keys
 * so the Marketplace API deletes them. Never writes listing.protectedData.
 */
export const withOnlineMeetingPrivacy = (updateValues = {}, existingListing) => {
  const existingPublic = existingListing?.attributes?.publicData || {};
  const existingPrivate = existingListing?.attributes?.privateData || {};
  const existingInvalidProtected = existingListing?.attributes?.protectedData || {};
  const incomingPublic = updateValues.publicData;
  const incomingPrivate = updateValues.privateData || {};
  const incomingInvalidProtected = updateValues.protectedData || {};

  const submittedValue =
    readMeetingValue(incomingPrivate) ??
    readMeetingValue(incomingInvalidProtected) ??
    readMeetingValue(incomingPublic) ??
    firstFormMeetingValue(updateValues);

  const submittedMeetingField = hasSubmittedMeetingField(
    updateValues,
    incomingPublic,
    incomingPrivate,
    incomingInvalidProtected
  );

  const migrateValue =
    submittedValue ??
    readMeetingValue(existingPrivate) ??
    readMeetingValue(existingInvalidProtected) ??
    readMeetingValue(existingPublic);

  const publicHasLeak =
    hasPublicMeetingLeak(incomingPublic) || hasPublicMeetingLeak(existingPublic);

  const shouldOverwritePrivate = submittedMeetingField;
  const shouldMigratePrivate =
    !submittedMeetingField && isPresent(migrateValue) && !isPresent(existingPrivate.onlineMeeting);

  const shouldClearPublic =
    publicHasLeak ||
    (incomingPublic && ONLINE_MEETING_PUBLIC_KEYS.some(key => key in incomingPublic));

  const hasIncomingProtectedKey = Object.prototype.hasOwnProperty.call(
    updateValues,
    'protectedData'
  );

  if (
    !shouldClearPublic &&
    !shouldOverwritePrivate &&
    !shouldMigratePrivate &&
    !hasIncomingProtectedKey
  ) {
    return updateValues;
  }

  const next = { ...updateValues };
  // Listings reject protectedData (validation-disallowed-key).
  delete next.protectedData;

  if (shouldClearPublic) {
    next.publicData = {
      ...(incomingPublic || {}),
      onlineMeeting: null,
      meetingLink: null,
    };
  }

  if (shouldOverwritePrivate) {
    next.privateData = {
      ...incomingPrivate,
      onlineMeeting: isPresent(submittedValue) ? submittedValue : null,
    };
  } else if (shouldMigratePrivate) {
    next.privateData = {
      ...incomingPrivate,
      onlineMeeting: migrateValue,
    };
  }

  return next;
};
